import React, { useState, useEffect } from 'react';
import { Grid, Menu, Card, Icon, Segment, Message, Button, Sidebar, SidebarPushable, SidebarPusher, MenuItem } from 'semantic-ui-react';

import { Filters } from '../search/Filtersnew';
import { Result } from '../search/Result';

export const ResultsContainer = ({ query, setQuery, optAggs, setOptAggs, model, aliases, start = 0, size = 10 }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aggs, setAggs] = useState({});
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [noResults, setNoResults] = useState(false);
  const totalPages = Math.ceil(total / size);
  const [filterError, setFilterError] = useState(""); // New state for filter errors

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const rebuildPagination = () => {
    const pageItems = [];

    const addPage = (page) => {
      pageItems.push(
        <Menu.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Menu.Item>
      );
    };

    const addEllipsis = (key) => { /** Create elipsis function */
      pageItems.push(
        <Menu.Item key={key} disabled>
          ...
        </Menu.Item>
      );
    };

    /** Chevron: Previous Item only if the page is past 1 */
    if (currentPage > 1) {
      pageItems.push(
        <Menu.Item key="prev" onClick={() => handlePageChange(currentPage - 1)}>
          <Icon name="chevron left" />
        </Menu.Item>
      );
    }

    /** First page is always there*/
    addPage(1);

    /** Show ellipsis only if we exceed three pages*/
    if (currentPage > 3) {
      addEllipsis("left-ellipsis");
    }

    /** Show page before current page (if > 1 and not already shown) */
    if (currentPage - 1 > 1) {
      addPage(currentPage - 1);
    }

    /** Show current page */
    if (currentPage !== 1 && currentPage !== totalPages) {
      addPage(currentPage);
    }

    /** Show page after current page (if < totalPages and not already shown) */
    if (currentPage + 1 < totalPages) {
      addPage(currentPage + 1);
    }

    /** Show ellipsis before last page if less than 2 pages from the end */
    if (currentPage < totalPages - 2) {
      addEllipsis("right-ellipsis");
    }

    /** Always show last page (if more than 1 page) */
    if (totalPages > 1) {
      addPage(totalPages);
    }

    /** Chevron: Next page action */
    if (currentPage < totalPages) {
      pageItems.push(
        <Menu.Item key="next" onClick={() => handlePageChange(currentPage + 1)}>
          <Icon name="chevron right" />
        </Menu.Item>
      );
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2em' }}>
        <Menu pagination>{pageItems}</Menu>
      </div>
    );
  };

  async function getData() {
    setNoResults(false);
    setLoading(true);
    const q = encodeURIComponent(query);
    const offset = (currentPage - 1) * size;

    console.debug(`Fetching page ${currentPage}, offset ${offset}`);

    const baseUrl = `/go/api/v1/search?q=${q}&model=${model}&start=${offset}&size=${size}`;

    let optAggsStr = "";
    console.debug("Optional Aggregations:", optAggs);

    // Made this change to deal with year range filters
    if (optAggs) {
      Object.entries(optAggs).forEach(([key, value]) => {

        if (value === null || value === undefined || value === "") return;
        
        // Date Filters with gte and lte (e.g., year range)
        if (
          typeof value === "object" &&
          value.gte != null &&
          value.lte != null
        ) { // Making sure invalid user input doesn't break the app and provides useful error messages to UI
          const minYear = 1200;  
          const maxYear = new Date().getFullYear();

          let gte = parseInt(value.gte, 10);
          let lte = parseInt(value.lte, 10);

          // clear previous error
          setFilterError("");

          if (isNaN(gte) || isNaN(lte)) {
            setFilterError("Year must be a number");
            return;
          }

          if (gte < 1000 || lte < 1000 || gte > 9999 || lte > 9999) {
            setFilterError("Year must be 4 digits (e.g., 1820)");
            return;
          }

          if (gte < minYear) {
            setFilterError(`Year cannot be before ${minYear}`);
            return;
          }

          if (lte > maxYear) {
            setFilterError("Year cannot be in the future");
            return;
          }

          if (gte > lte) {
            setFilterError("Start year cannot be greater than end year");
            return;
          }

          optAggsStr += `&${key}=${encodeURIComponent(`${gte}-${lte}`)}`;
          return;
        }

        // Categorical filters with multiple values
        if (Array.isArray(value)) {
          optAggsStr += `&${key}=${encodeURIComponent(value.join(','))}`;
          return;
        }

        // Categorical filters with single value
        optAggsStr += `&${key}=${encodeURIComponent(value)}`;
      });
    }

    console.debug(`Constructed URL: ${baseUrl + optAggsStr}`);

    const result = await fetch(baseUrl + optAggsStr);
    
    const json = await result.json();
    console.debug(json);

    setAggs(json.aggregations);

    if (!json.hits || json.hits.hits.length === 0) {
      setNoResults(true);
      setResults([]);
      setLoading(false);
      return;
  }

  setTotal(json.hits.total);

  const cards = json.hits.hits.map((h, i) => (
    <Result data={h} alias={model} key={i} />
  ));
  setResults(cards);
  setLoading(false);
  }

// Reset filters and pagination when switching between different models (songs, composers, etc.)

  useEffect(() => { // reset filters when switching between songs, composers or other tabs
    setOptAggs({});
    getData();
    setCurrentPage(1);
    setNoResults(false);
    setResults([]);
  }, [model]);

  useEffect(() => { // fetch data with no Optional aggs
    getData();
  }, [query, model, aliases, currentPage, optAggs]);

  useEffect(() => { // reset pagination when query changes
    setCurrentPage(1);
  }, [query]);

  const [visible, setVisible] = useState(false);

  return (
    <Segment attached='bottom' loading={loading}>

    <Button
      onClick={() => setVisible(!visible)}
      icon
      labelPosition="left"
    >
      <Icon name="filter" />
      {visible ? "Hide Filters" : "Show Filters"}
    </Button>

    <SidebarPushable as={Segment}>

      <Sidebar
        as={Menu}
        animation="push"
        onHide={() => setVisible(false)}
        vertical
        visible={visible}
        width="wide"
      >
        <MenuItem>
          <Filters
            aggs={aggs}
            aliases={aliases}
            setQuery={setQuery}
            query={query}
            optAggs={optAggs}
            setOptAggs={setOptAggs}
            filterError={filterError}
          />
        </MenuItem>
      </Sidebar>

      <SidebarPusher>
        <Segment basic style={{ minHeight: "1500px" }}>

          {total > size && rebuildPagination()}

          {noResults ? (
            <Message info>
              No results found.
            </Message>
          ) : (
            <Card.Group itemsPerRow={4}>
              {results}
            </Card.Group>
          )}

        </Segment>
      </SidebarPusher>

    </SidebarPushable>

    </Segment>
  );
};
